import { parseDate } from "@internationalized/date";
import { CalendarBlank, CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { getLocale } from "@plannello/i18n/runtime";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  Dialog,
  DialogTrigger,
  Heading,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import { Popover } from "./Popover";

/**
 * A date property: a quiet button showing the date (or a placeholder) that opens a calendar.
 * Values are calendar dates "YYYY-MM-DD"; `null` clears. Used in the item sidebar where a typed
 * date field would be noisy; the calendar is fully keyboard operable (arrows, Page Up/Down).
 */
export function DateButton({
  value,
  onChange,
  label,
  placeholder,
  className,
  isReadOnly,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  /** Accessible name, e.g. "Due date". */
  label: string;
  placeholder: string;
  className?: string;
  /** Show the date as plain text (no calendar). */
  isReadOnly?: boolean;
}) {
  const shown = value
    ? new Intl.DateTimeFormat(getLocale(), { day: "numeric", month: "short", year: "numeric" }).format(
        new Date(`${value}T00:00:00`),
      )
    : null;
  if (isReadOnly) {
    return (
      <span
        className={cx(
          "flex min-h-(--control-h-md) w-full min-w-0 items-center gap-2 px-2 text-left text-sm",
          shown ? "text-fg" : "text-fg-muted",
          className,
        )}
      >
        <CalendarBlank size={14} aria-hidden="true" className="shrink-0 text-fg-subtle" />
        <span className="sr-only">{label}: </span>
        <span className="truncate-title tnum">{shown ?? placeholder}</span>
      </span>
    );
  }
  return (
    <DialogTrigger>
      <AriaButton
        aria-label={`${label}: ${shown ?? placeholder}`}
        className={cx(
          "press transition-ui flex min-h-(--control-h-md) w-full min-w-0 items-center gap-2 rounded-control px-2 text-left text-sm outline-none",
          "data-hovered:bg-hover data-pressed:bg-active data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:-outline-offset-2 data-focus-visible:outline-focus",
          shown ? "text-fg" : "text-fg-muted",
          className,
        )}
      >
        <CalendarBlank size={14} aria-hidden="true" className="shrink-0 text-fg-subtle" />
        <span className="truncate-title tnum">{shown ?? placeholder}</span>
      </AriaButton>
      <Popover placement="bottom start" className="p-0">
        <Dialog aria-label={label} className="p-3 outline-none">
          {({ close }) => (
            <>
              <Calendar
                aria-label={label}
                value={value ? parseDate(value) : null}
                onChange={(d) => {
                  onChange(d.toString());
                  close();
                }}
                className="w-64"
              >
                <header className="mb-2 flex items-center gap-1">
                  <AriaButton
                    slot="previous"
                    className="flex size-7 items-center justify-center rounded-control text-fg-muted outline-hidden data-hovered:bg-hover data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
                  >
                    <CaretLeft size={14} aria-hidden="true" />
                  </AriaButton>
                  <Heading className="flex-1 text-center text-sm font-medium text-fg" />
                  <AriaButton
                    slot="next"
                    className="flex size-7 items-center justify-center rounded-control text-fg-muted outline-hidden data-hovered:bg-hover data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
                  >
                    <CaretRight size={14} aria-hidden="true" />
                  </AriaButton>
                </header>
                <CalendarGrid className="w-full border-collapse">
                  <CalendarGridHeader>
                    {(day) => (
                      <CalendarHeaderCell className="pb-1 text-2xs font-medium text-fg-subtle">
                        {day}
                      </CalendarHeaderCell>
                    )}
                  </CalendarGridHeader>
                  <CalendarGridBody>
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className={cx(
                          "m-px flex size-8 cursor-default items-center justify-center rounded-control text-sm text-fg outline-none tnum",
                          "data-hovered:bg-hover data-outside-month:text-fg-subtle data-outside-month:opacity-50",
                          "data-selected:bg-accent data-selected:text-accent-fg data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
                          "data-today:font-semibold data-today:underline data-today:underline-offset-4",
                        )}
                      />
                    )}
                  </CalendarGridBody>
                </CalendarGrid>
              </Calendar>
              {value ? (
                <AriaButton
                  onPress={() => {
                    onChange(null);
                    close();
                  }}
                  className="mt-2 flex h-(--control-h-sm) items-center gap-1.5 rounded-control px-2 text-xs text-fg-muted outline-hidden data-hovered:bg-hover data-hovered:text-fg data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
                >
                  <X size={12} aria-hidden="true" />
                  {m.date_clear()}
                </AriaButton>
              ) : null}
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
